import type { TFunction } from 'i18next';
import { translateDynamic } from '../i18n/dynamic';

export interface NotificationI18n {
  key: 'created' | 'cancelled' | 'rescheduled' | 'attended';
  params: { name: string; service: string; startDate?: string | null; timeZone?: string };
}

const WHEN_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
};

// Notifications written before the structured `i18n` field existed only carry
// English text, e.g. "Jane Doe booked Deep Clean" / "Scheduled for Sunday, ...".
const LEGACY_TITLE = /^(.+?) (booked|cancelled|rescheduled) (.+)$/;
const LEGACY_KEY: Record<string, NotificationI18n['key']> = {
  booked: 'created', cancelled: 'cancelled', rescheduled: 'rescheduled',
};
const LEGACY_MESSAGE = /^(Scheduled for|Originally scheduled for|New time:) (.+)\.$/;

function formatWhen(lng: string, iso?: string | null, timeZone?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(lng, { ...WHEN_FORMAT, timeZone }).format(d);
}

/** Wall-clock text like "Sunday, October 25, 2026 at 12:30 PM" -> localized, no tz shift. */
function reformatLegacyWhen(lng: string, text: string): string {
  const d = new Date(text.replace(' at ', ' '));
  return Number.isNaN(d.getTime()) ? text : new Intl.DateTimeFormat(lng, WHEN_FORMAT).format(d);
}

export function localizeNotification(
  t: TFunction,
  lng: string,
  n: { title: string; message: string; i18n?: NotificationI18n },
): { title: string; message: string } {
  if (n.i18n) {
    const { key, params } = n.i18n;
    const when = formatWhen(lng, params.startDate, params.timeZone);
    return {
      title: t(`notif.${key}.title`, { name: params.name, service: translateDynamic(params.service) }),
      message: when
        ? t(`notif.${key}.message`, { when })
        : t(`notif.${key}.noDate`, { service: translateDynamic(params.service) }),
    };
  }

  const tm = LEGACY_TITLE.exec(n.title);
  if (!tm) return { title: n.title, message: n.message };
  const key = LEGACY_KEY[tm[2]];
  const mm = LEGACY_MESSAGE.exec(n.message);
  return {
    title: t(`notif.${key}.title`, { name: tm[1], service: translateDynamic(tm[3]) }),
    message: mm ? t(`notif.${key}.message`, { when: reformatLegacyWhen(lng, mm[2]) }) : n.message,
  };
}
